import { siteTexts } from "../../../shared/i18n/siteTexts";
import { parseHourMinuteToDuration } from "../../../shared/utils/dateDrafts";
import type {
  ChallengeDetail,
  UpdateChallengePayload,
} from "../../challenges/types/challengeAdmin";
import type { DraftPair } from "../../challenges/types/challengeDraft";
import { normalizePocoCourtsCount } from "../../challenges/utils/pocoSchedule";
import {
  buildTournamentCategoriesPayload,
  normalizeChallengeRegistrationCategoriesForEdit,
  normalizeTournamentShirtSizes,
  type TournamentRegistrationCategoryItem,
} from "./tournamentCategories";
import {
  availabilityRestrictionTypesFromPeriods,
  parseAvailabilityPeriodLines,
} from "../../tournamentAvailability/tournamentAvailabilitySettings";
import { parseAmountToCents } from "./adminChallengeEditor";

const MAX_INTEGER_CENTS = 2147483647;

export function validateChallengeScheduleDraft(input: {
  challengeDurationDraft: string;
  challengeScheduledAtDraft: Date | null;
  challengeScheduledEndAtDraft: Date | null;
  showErrorToast: (message: string) => void;
}) {
  const t = siteTexts.admin;
  const {
    challengeDurationDraft,
    challengeScheduledAtDraft,
    challengeScheduledEndAtDraft,
    showErrorToast,
  } = input;
  if (
    challengeScheduledAtDraft &&
    Number.isNaN(challengeScheduledAtDraft.getTime())
  ) {
    showErrorToast(t.validation.invalidDateTime);
    return false;
  }
  if (
    challengeScheduledEndAtDraft &&
    Number.isNaN(challengeScheduledEndAtDraft.getTime())
  ) {
    showErrorToast(t.validation.invalidEndDate);
    return false;
  }
  if (
    challengeScheduledAtDraft &&
    challengeScheduledEndAtDraft &&
    challengeScheduledEndAtDraft.getTime() < challengeScheduledAtDraft.getTime()
  ) {
    showErrorToast(t.validation.endDateBeforeStart);
    return false;
  }
  if (challengeDurationDraft.trim()) {
    const parsed = parseHourMinuteToDuration(challengeDurationDraft);
    if (parsed === null) {
      showErrorToast(t.validation.invalidDuration);
      return false;
    }
  }
  return true;
}

export function buildChallengePersistencePayload({
  challengeDetail,
  challengeEventDraft,
  draftPairs,
  organizerUserId,
  participantCpfs,
}: {
  challengeDetail: ChallengeDetail;
  challengeEventDraft?: {
    shirtSizesEnabled?: boolean;
    shirtSizes?: string[];
    availabilityRestrictionsEnabled?: boolean;
    availabilityRestrictionAmount?: string;
    availabilityRestrictionLimit?: string;
    availabilityRestrictionDeadlineAt?: string;
    availabilityRestrictionTypes?: string[];
    availabilityRestrictionPeriodsText?: string;
  };
  draftPairs: DraftPair[];
  allowTnpEventIdentity?: boolean;
  organizerUserId: number | null;
  participantCpfs: string[];
}): UpdateChallengePayload {
  const isMixed =
    (challengeDetail.challengeType === "super8"
      ? challengeDetail.categoryGender || "mixed"
      : challengeDetail.categoryGender) === "mixed" ||
    challengeDetail.categoryGender === "open";
  const isTournament =
    String(challengeDetail.challengeType || "").trim().toLowerCase() ===
    "tournament";
  const usesRegistrationCategories =
    isTournament ||
    ["challenge", "poco"].includes(
      String(challengeDetail.challengeType || "").trim().toLowerCase(),
    );
  const registrationCategoriesForPayload: TournamentRegistrationCategoryItem[] | undefined =
    usesRegistrationCategories && !isTournament
      ? normalizeChallengeRegistrationCategoriesForEdit(challengeDetail)
      : challengeDetail.registrationCategories;
  const parseMoneyToCents = (value?: string) => {
    const cents = parseAmountToCents(value || "") ?? 0;
    return cents <= MAX_INTEGER_CENTS ? cents : null;
  };
  const availabilityRestrictionsEnabled = Boolean(
    challengeEventDraft?.availabilityRestrictionsEnabled,
  );

  return {
    groupName: challengeDetail.groupName,
    challengeName: challengeDetail.challengeName,
    description: challengeDetail.description,
    address: challengeDetail.address,
    city: challengeDetail.city,
    state: challengeDetail.state,
    shirtSizesEnabled: Boolean(challengeEventDraft?.shirtSizesEnabled),
    shirtSizes: challengeEventDraft?.shirtSizesEnabled
      ? normalizeTournamentShirtSizes(challengeEventDraft.shirtSizes)
      : [],
    categoryGender:
      challengeDetail.challengeType === "super8"
        ? challengeDetail.categoryGender || "mixed"
        : challengeDetail.categoryGender || null,
    category: isMixed ? null : challengeDetail.category,
    categoryMale: isMixed ? challengeDetail.categoryMale : null,
    categoryFemale: isMixed ? challengeDetail.categoryFemale : null,
    super8Group: null,
    participantCpfs,
    pairIds: draftPairs.map((pair) => pair.id),
    challengeModel: challengeDetail.challengeModel || "tnp_standard",
    gameType:
      challengeDetail.challengeModel === "custom"
        ? challengeDetail.gameType || "games"
        : null,
    gameCount:
      challengeDetail.challengeModel === "custom" &&
      (challengeDetail.gameType || "games") === "games"
        ? Math.floor(Number(challengeDetail.gameCount || 0))
        : null,
    gameDurationMinutes:
      challengeDetail.challengeModel === "custom" &&
      challengeDetail.gameType === "time"
        ? Math.floor(Number(challengeDetail.gameDurationMinutes || 0))
        : null,
    courtsCount:
      challengeDetail.challengeType === "poco" ||
      (challengeDetail.challengeModel === "custom" &&
        challengeDetail.challengeType !== "super8")
        ? challengeDetail.challengeType === "poco"
          ? normalizePocoCourtsCount(challengeDetail.courtsCount)
          : Math.max(2, Math.floor(Number(challengeDetail.courtsCount || 2)))
        : null,
    playersCount:
      challengeDetail.challengeModel === "custom" ||
      challengeDetail.challengeType === "poco"
        ? challengeDetail.challengeType === "super8"
          ? 8
          : challengeDetail.challengeType === "poco"
            ? normalizePocoCourtsCount(challengeDetail.courtsCount) * 4
            : Math.max(
                12,
                Math.floor(Number(challengeDetail.playersCount || 12)),
              )
        : null,
    scheduledAt: challengeDetail.scheduledAt,
    scheduledEndAt: challengeDetail.scheduledEndAt || null,
    durationSeconds: Number(challengeDetail.durationSeconds || 0),
    isOfficial: Boolean(challengeDetail.isOfficial ?? true),
    includeInRanking: isTournament
      ? Boolean(challengeDetail.isOfficial ?? true)
        ? Boolean(challengeDetail.includeInRanking ?? false)
        : true
      : Boolean(challengeDetail.includeInRanking ?? true),
    eventIdentityMode: challengeDetail.isOfficial
      ? "tnp_standard"
      : challengeDetail.eventIdentityMode === "tnp_standard"
        ? "organizer_photo"
        : challengeDetail.eventIdentityMode || "organizer_photo",
    availabilityRestrictionsEnabled,
    availabilityRestrictionAmountCents: availabilityRestrictionsEnabled
      ? parseMoneyToCents(challengeEventDraft?.availabilityRestrictionAmount)
      : null,
    availabilityRestrictionLimit: availabilityRestrictionsEnabled
      ? Math.max(
          1,
          Math.floor(
            Number(challengeEventDraft?.availabilityRestrictionLimit || 1),
          ),
        )
      : null,
    availabilityRestrictionDeadlineAt: null,
    availabilityRestrictionTypes: availabilityRestrictionsEnabled
      ? availabilityRestrictionTypesFromPeriods(
          parseAvailabilityPeriodLines(
            challengeEventDraft?.availabilityRestrictionPeriodsText || "",
          ),
        )
      : [],
    availabilityRestrictionPeriods: availabilityRestrictionsEnabled
      ? parseAvailabilityPeriodLines(
          challengeEventDraft?.availabilityRestrictionPeriodsText || "",
        )
      : [],
    ...(usesRegistrationCategories
      ? {
          tournamentCategories: buildTournamentCategoriesPayload(
            registrationCategoriesForPayload,
          ),
        }
      : {}),
    organizerUserId,
  };
}
